import Enumerable from 'linq';
import { DateTime } from 'luxon';
import { RRule, rrulestr } from 'rrule';
import { v4 } from 'uuid';

import { Allocation, AllocationCard, AllocationRule, AllocationStatus, AllocationWithMeta, ContactEntity, DateUtility, JobUtility, Label, SchedulerOpportunitySummary, SchedulerWorkSummary, Source, tryCatch, WorkOrderSummary } from '@jms/model';

import { AllocationEditFormInput, AllocationEditType } from '../model';
import { AllocationService } from '../services';

export type AllocationLike = Allocation | AllocationWithMeta | AllocationCard;

export class AllocationUtility {

    public static allocationFromWorkSummary(item: SchedulerWorkSummary, id?: string, resource: ContactEntity = null, source = Source.Web): AllocationWithMeta {

        id = id ?? v4();

        const safeResource = source == Source.Web
            ? item?.contractor ? item.contractor : resource
            : resource;

        return {
            _id: id,
            resource: safeResource,
            run: null,
            status: AllocationStatus.Open,
            startDate: null,
            endDate: null,
            booking: null,
            job: {
                _id: item.job._id,
                name: item.job.name
            },
            workOrder: {
                _id: item._id,
                name: item.workOrderNumber
            },
            site: item.site,
            clusterColor: item.clusterColor,
            overhead: null,
            source: source,
            meta: {
                type: item.jobType,
                priority: item.priority,
                tags: item?.tags,
                customer: item?.customer?.name,
                customerWO: item?.customerWO,
                category: item?.category,
                status: item?.status,
                site: {
                    _id: item?.site?._id,
                    address: JobUtility.address(item?.site?.address),
                    contactName: item?.site?.contactName,
                    contactPhone: item?.site?.contactPhone
                }

            },
            estimatedDuration: item.estimatedDuration
        } as AllocationWithMeta;
    };

    public static allocationFromOpportunity(item: SchedulerOpportunitySummary, id?: string, resource: ContactEntity = null, source = Source.Web): Allocation {

        id = id ?? v4();

        return {
            _id: id,
            resource,
            run: null,
            status: AllocationStatus.Open,
            startDate: null,
            endDate: null,
            booking: null,
            opportunity: {
                _id: item._id,
                name: item.opportunityNumber
            },
            site: item.site,
            source: source,
        } as Allocation;
    };

    public static allocationFromWorkOrderSummary(item: WorkOrderSummary, id?: string, resource: ContactEntity = null, source = Source.Web): Allocation {

        id = id ?? v4();

        return {
            _id: id,
            resource: resource,
            run: null,
            status: AllocationStatus.Open,
            startDate: null,
            endDate: null,
            booking: null,
            job: {
                _id: item.jobId,
                name: item.jobNumber
            },
            workOrder: {
                _id: item._id,
                name: item.workOrderNumber
            },
            site: item.site,
            clusterColor: '#ffffff',
            overhead: null,
            source: source
        } as Allocation;
    };

    public static allocationFromOverhead(item: Label, id?: string, timezone?: string, resource: ContactEntity = null, source = Source.Web,): Allocation {

        id = id ?? v4();

        return {
            _id: id,
            resource: resource,
            run: null,
            status: AllocationStatus.Overhead,
            startDate: null,
            endDate: null,
            overhead: item,
            timezone: timezone,

            booking: null,
            job: null,
            workOrder: null,
            site: null,
            clusterColor: null,
            source: source
        } as Allocation
    };

    public static canDeleteAllocation(source: Source, allocationSource: Source): boolean {

        switch (source) {
            case Source.Web: {
                const allowSource = [
                    Source.Web,
                    Source.Mobile
                ];

                return allowSource.includes(allocationSource);
            }

            case Source.ContractorPortal: {
                const allowSource = [
                    Source.ContractorPortal,
                    Source.Mobile
                ];

                return allowSource.includes(allocationSource);
            }
        }

    }

    // Resource-change permission. Distinct from canDeleteAllocation:
    // Contractor Portal must be able to assign resources on allocations
    // the tenant handed off (Source.Web) without gaining the right to
    // delete them. Web branch mirrors canDeleteAllocation to keep the
    // shared resource-picker template byte-identical for tenant users.
    public static canChangeAllocationResource(source: Source, allocationSource: Source): boolean {

        switch (source) {
            case Source.Web: {
                const allowSource = [
                    Source.Web,
                    Source.Mobile
                ];

                return allowSource.includes(allocationSource);
            }

            case Source.ContractorPortal: {
                const allowSource = [
                    Source.ContractorPortal,
                    Source.Mobile,
                    Source.Web
                ];

                return allowSource.includes(allocationSource);
            }
        }

    }

    public static isShadow(allocation: AllocationLike): boolean {
        if (!allocation?.run?._id)
            return false;

        return true; // allocation?.run?._id !== allocation?.resource?._id;
    }

    public static isRecurrence(allocation: AllocationLike): boolean {
        return !!allocation?.rule || !!allocation?.parentId;
    }

    /**
     * True when this card cannot be edited on the board because it is a projected recurrence: a virtual
     * subject that is also a rule or one of its occurrences. It does not exist as a row, so there is
     * nothing for an edit or a drag to write to.
     *
     * Both halves are needed. A Visit-backed card carries `virtualEntity` but is one concrete dated row a
     * dispatcher reschedules (WBS-11002). A recurring ad-hoc appointment carries `rule` / `parentId` but is
     * a stored series the editor splits by scope (This / This and future / All); gating on recurrence alone
     * made those uneditable and undraggable (WBS-13399).
     */
    public static isReadOnlyCard(allocation: AllocationLike): boolean {
        return !!allocation?.virtualEntity
            && AllocationUtility.isRecurrence(allocation);
    }

    public static async getRecurrenceAllocations(editType: AllocationEditType, allocation: Allocation, allocationService: AllocationService): Promise<{ selectedAllocation: Allocation; allocations: AllocationEditFormInput[] }> {

        const buildRule = (rule: RRule, exclude: Date[] = []): AllocationRule => ({
            _id: rule.toString(),
            name: rule.toText(),
            start: rule.origOptions.dtstart,
            end: rule.origOptions.until,
            exclude,
        });

        const { _id, startDate, parentId, rule, overhead } = allocation;


        // Truly standalone overhead — never part of a series, no exception state. editCategory 'modified'
        // keeps an edit to untracked fields (overhead title/description) in the save batch once the board
        // card carries createdAt and the appointment stops reading as new (WBS-13435) — mirrors the detached branch below.
        if (!rule && !parentId) {
            return {
                selectedAllocation: allocation,
                allocations: [{
                    ...allocation,
                    editCategory: 'modified',
                    canRecurrence: true
                }]
            }
        }

        // Re-opened split exception (parentId set, rule null). The double-click path
        // skips the scope dialog for these, so editType is undefined (WBS-8547). It is
        // a detached single event — the origin already excludes this date from the
        // first split, so edit it in place. editCategory 'modified' forces it into the
        // save batch even when only untracked fields (overhead title/description) change
        // — mirrors the This branch. canRecurrence false: a detached instance cannot
        // re-spawn a series.
        if (!rule && parentId && !editType) {
            return {
                selectedAllocation: allocation,
                allocations: [{
                    ...allocation,
                    editCategory: 'modified',
                    canRecurrence: false
                }]
            }
        }


        const [error, allRelatedAllocations] = await tryCatch(allocationService.getByInstanceId(overhead?.instanceId).toPromise());

        if (error) throw error;

        const originAllocation = allRelatedAllocations?.find(x => !x.parentId);

        if (!originAllocation) throw 'No origin allocation found';


        // // if select this and future, but origin allocation is selected, then change to all
        const originDate = DateUtility.getStartOfDay(originAllocation.startDate);
        const selectedDate = DateUtility.getStartOfDay(startDate);
        if (originAllocation._id === _id && editType === AllocationEditType.ThisAndFuture && originDate.equals(selectedDate)) {
            editType = AllocationEditType.All;
        }


        switch (editType) {
            case AllocationEditType.This: {

                const thisAllocation: AllocationEditFormInput = !rule && parentId
                    ? {
                        ...allocation,
                        editCategory: 'modified',
                        rule: null,
                        canRecurrence: false,
                    }
                    : {
                        ...allocation,
                        _id: v4(),
                        parentId: originAllocation._id,
                        rule: null,
                        createdAt: null, // for trigger create new
                        editCategory: 'added',
                        canRecurrence: false,
                    };

                const originRule = rrulestr(originAllocation.rule._id).clone();
                const excludeDate = this.getDateUTC(startDate, originRule.origOptions.tzid);

                // update old allocation with rule exclude date is this.allocation.date
                const updatedOriginAllocation: AllocationEditFormInput = {
                    ...originAllocation,
                    rule: {
                        ...originAllocation.rule,
                        exclude: Enumerable
                            .from(originAllocation.rule.exclude)
                            .concat([excludeDate])
                            .where(x => !!x)
                            .distinct()
                            .toArray(),
                    },
                    editCategory: 'modified',
                    hide: true,
                }

                return {
                    selectedAllocation: thisAllocation,
                    allocations: [updatedOriginAllocation, thisAllocation]
                };

            }

            case AllocationEditType.ThisAndFuture: {

                // create new allocation with new rule start this.allocation.date
                const newRule = rrulestr(originAllocation.rule._id).clone();
                const oldRule = rrulestr(originAllocation.rule._id).clone();

                const newDate = this.getDateUTC(startDate, newRule.origOptions.tzid);
                newRule.origOptions.dtstart = newDate;

                if (oldRule.origOptions.count) {
                    const count = this.getCount(oldRule, newDate);
                    newRule.origOptions.count = count;
                    newRule.options.count = count;
                }

                const newAllocation: AllocationEditFormInput = {
                    ...allocation,
                    _id: v4(),
                    rule: buildRule(newRule),
                    parentId: null,
                    createdAt: null,
                    updatedAt: null,
                    editCategory: 'added',
                    overhead: {
                        ...overhead,
                        instanceId: v4(), // unlink with old one
                    }
                }

                let updatedOriginAllocation: AllocationEditFormInput = {
                    ...originAllocation,
                    hide: true,
                    editCategory: 'deleted',
                };


                const newStartDate = DateTime.fromJSDate(new Date(startDate)).startOf('day');
                const oldStartDate = DateTime.fromJSDate(new Date(originAllocation.startDate)).startOf('day');
                if (!newStartDate.equals(oldStartDate)) {
                    // update old allocation with rule end date this.allocation.date
                    const starOfDay = DateTime.fromJSDate(new Date(startDate)).setZone(oldRule.origOptions.tzid).startOf('day');
                    const until = oldRule.before(starOfDay.toJSDate());

                    // Drop the old rule entirely when truncation would leave no surviving
                    // instances — the new rule fully replaces it (WBS-8505).
                    if (until) {
                        oldRule.origOptions.until = this.getDateUTC(until, newRule.origOptions.tzid);

                        updatedOriginAllocation = {
                            ...originAllocation,
                            rule: buildRule(oldRule, originAllocation.rule.exclude),
                            editCategory: 'modified',
                            hide: true,
                        }
                    }
                }

                // delete all allocations has changed in this rule after this.allocation.date
                const allocations = Enumerable
                    .from(allRelatedAllocations)
                    .where(x => x._id !== originAllocation._id && x.startDate >= startDate)
                    .select(x => {
                        return {
                            ...x,
                            editCategory: 'deleted',
                            hide: true,
                            canRecurrence: false,
                        } as AllocationEditFormInput
                    })
                    .concat([updatedOriginAllocation, newAllocation])
                    .toArray();


                return {
                    selectedAllocation: newAllocation,
                    allocations
                }
            }


            case AllocationEditType.All: {
                const rule = rrulestr(originAllocation.rule._id).clone();

                // "All" applies only to the origin's remaining members. Siblings sharing
                // instanceId are detached ("This instance") standalones — never delete them.
                // Preserve the origin's exclude so detached dates don't resurrect as
                // duplicate occurrences.
                return {
                    selectedAllocation: originAllocation,
                    allocations: [{
                        ...originAllocation,
                        rule: buildRule(rule, originAllocation.rule.exclude ?? []),
                        editCategory: 'modified',
                        canRecurrence: true
                    } as AllocationEditFormInput]
                };
            }

            // Unrecognised editType must never fall through to an undefined return —
            // the editor reads result.allocations and would crash. Treat as a
            // single-instance edit; editCategory 'modified' keeps untracked-field
            // edits (overhead title/description) in the save batch.
            default:
                return {
                    selectedAllocation: allocation,
                    allocations: [{
                        ...allocation,
                        editCategory: 'modified',
                        canRecurrence: false
                    }]
                };
        }
    }

    private static getCount(rule: RRule, startDate: Date): number {
        const allDate = rule.all();
        if (!allDate?.length) {
            return 0;
        }

        const starOfDay = DateTime.fromJSDate(new Date(startDate)).startOf('day')
        const after = allDate[allDate.length - 1];
        const before = starOfDay.toJSDate();
        const count = rule.between(before, after, true);

        return count.length;
    }

    private static getDateUTC(date: Date, tzid: string): Date {
        const { year, month, day } = DateUtility.getDateTime(date, tzid);
        return DateTime.utc(year, month, day, 0, 0, 0, 0).toJSDate();
    }
}